/**
 * Typed wrapper around the model worker.
 *
 * Deliberately not a React hook and not React state: tokens arrive faster
 * than React should re-render, so callers get a callback per chunk and decide
 * for themselves how often to paint. Same off-React rule as the swarm bays.
 */

import type {
  ChatMessage,
  FromWorker,
  GenStats,
  GenerateOpts,
  ImageOpts,
  ImageResult,
  ToWorker,
} from "./protocol";

export type LoadHandlers = {
  onProgress?: (file: string, loaded: number, total: number, pct: number) => void;
  onMessage?: (message: string) => void;
};

export type StreamHandlers = {
  onStart?: () => void;
  onToken?: (text: string) => void;
};

/**
 * One generation, in full: exactly what the model was given and what it said.
 *
 * Emitted twice, once with `done: false` the moment the request is sent and
 * once when it finishes. The first emission is what lets the panel show the
 * input WHILE the model is still working on it, which is the interesting
 * moment and the one a completed-only log would miss.
 */
/**
 * What kind of turn this was. Drives colour and containment in the lab.
 *
 * Free reads this system by pattern rather than by prose: "I analyze it with
 * patterns and input/output coherence". A flat chronological list forces the
 * shape to be reconstructed from labels, sentence by sentence, which is the one
 * way of reading it that does not suit him. Kind and speaker are carried as
 * DATA so the layout can be structural instead of inferred from a string.
 */
export type TurnKind =
  /** A private note. Nobody but this agent ever sees it. */
  | "think"
  /** A spoken turn: the only thing the other agent will read. */
  | "output"
  /** Setup, before anyone has spoken. */
  | "agenda"
  /** Housekeeping the harness did, like folding notes to fit the window. */
  | "system";

export type GenerationRecord = {
  id: number;
  /** Which stage this was, when the caller said. */
  label?: string;
  /** Which agent produced it, by pack key. Absent for harness-owned work. */
  speaker?: string;
  kind?: TurnKind;
  /**
   * Which weights answered, by catalogue key.
   *
   * Only interesting when the two speakers run different models, which is the
   * point of allowing it: with a shared interpreter every difference between
   * the two sides is the lattice, and the moment that stops being true the log
   * has to say so on every card rather than in a header someone has scrolled
   * past.
   */
  model?: string;
  /** The messages as handed to `generate`, snapshotted. */
  messages: ChatMessage[];
  opts: GenerateOpts;
  startedAt: number;
  done: boolean;
  /**
   * The EXACT token stream, decoded, special tokens intact.
   *
   * Comes back from the worker, because the chat template is applied there.
   * Anything rebuilt on this side would be a reconstruction: Gemma's template
   * has no system role and folds the system message into the first user turn,
   * so a rebuild would show a system turn that never existed.
   */
  prompt?: string;
  text?: string;
  stats?: GenStats;
  ms?: number;
  error?: string;
};

/** What a call site knows about a turn. The model is added by the client. */
export type TurnMeta = { speaker?: string; kind?: TurnKind };

/**
 * Generation ids are GLOBAL, not per client, and that is load-bearing.
 *
 * The id serves two masters. The worker protocol only needs it unique per
 * worker, and a per-instance counter satisfied that for as long as there was
 * one client. The lab keys its generation log on the same id, and `absorb()`
 * treats a repeated id as the completion of an earlier record.
 *
 * So the moment a second model exists, two clients both start at 1, and every
 * card agent B produces overwrites the matching card from agent A. Nothing
 * throws; the log just quietly loses half the run. Global ids satisfy both
 * requirements, since unique everywhere implies unique per worker.
 */
let nextGenerationId = 1;

export class MindClient {
  private worker: Worker;
  /**
   * Which weights this client holds, once loaded.
   *
   * Stamped onto every record so the log says what produced each turn. With one
   * shared model that was implicit and a header would have done; with a model
   * per speaker, a card that does not name its model is unreadable.
   */
  modelKey: string | null = null;
  private taps = new Set<(rec: GenerationRecord) => void>();
  private pendingLabel: string | undefined;
  private pendingMeta: TurnMeta = {};
  private pending = new Map<
    number,
    {
      resolve: (r: { text: string; stats: GenStats }) => void;
      reject: (e: Error) => void;
      h: StreamHandlers;
      sent: ChatMessage[];
      opts: GenerateOpts;
      label?: string;
      meta: TurnMeta & { model?: string };
      startedAt: number;
    }
  >();
  /**
   * Image requests wait in their own map.
   *
   * They share the id counter, so nothing can collide, but they resolve with
   * pixels rather than with text and stats. A union would let a caller await a
   * picture and be handed a string.
   */
  private pendingImages = new Map<
    number,
    { resolve: (r: { images: ImageResult[]; ms: number }) => void; reject: (e: Error) => void }
  >();
  private loadWaiter: { resolve: (ms: number) => void; reject: (e: Error) => void; h: LoadHandlers } | null = null;

  constructor() {
    this.worker = new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });
    this.worker.addEventListener("message", (ev: MessageEvent<FromWorker>) => this.onMessage(ev.data));
    this.worker.addEventListener("error", (ev) => {
      const err = new Error(`worker crashed: ${ev.message}`);
      this.loadWaiter?.reject(err);
      this.loadWaiter = null;
      for (const [, p] of this.pending) p.reject(err);
      this.pending.clear();
      // Image requests are in their own map and would otherwise hang forever
      // on a worker crash.
      for (const [, p] of this.pendingImages) p.reject(err);
      this.pendingImages.clear();
    });
  }

  private onMessage(m: FromWorker) {
    switch (m.type) {
      case "progress":
        this.loadWaiter?.h.onProgress?.(m.file, m.loaded, m.total, m.pct);
        break;
      case "loading":
        this.loadWaiter?.h.onMessage?.(m.message);
        break;
      case "ready":
        this.loadWaiter?.resolve(m.loadMs);
        this.loadWaiter = null;
        break;
      case "start":
        this.pending.get(m.id)?.h.onStart?.();
        break;
      case "images": {
        const p = this.pendingImages.get(m.id);
        this.pendingImages.delete(m.id);
        p?.resolve({ images: m.images, ms: m.ms });
        break;
      }
      case "token":
        this.pending.get(m.id)?.h.onToken?.(m.text);
        break;
      case "done": {
        const p = this.pending.get(m.id);
        this.pending.delete(m.id);
        if (p) {
          this.taps.forEach((fn) =>
            fn({
              id: m.id,
              label: p.label,
              ...p.meta,
              messages: p.sent,
              opts: p.opts,
              startedAt: p.startedAt,
              done: true,
              prompt: m.prompt,
              text: m.text,
              stats: m.stats,
              ms: performance.now() - p.startedAt,
            }),
          );
        }
        p?.resolve({ text: m.text, stats: m.stats });
        break;
      }
      case "error": {
        const err = new Error(m.message);
        if (m.id !== undefined) {
          // The id belongs to exactly one of the two maps, and an error must
          // reject whichever it is or that caller waits forever.
          const p = this.pending.get(m.id);
          const pi = this.pendingImages.get(m.id);
          this.pending.delete(m.id);
          this.pendingImages.delete(m.id);
          // A failed turn must still close in the panel. Otherwise it sits
          // there mid-generation forever and reads as a hang rather than an
          // error, which is the harder of the two to diagnose.
          if (p) {
            this.taps.forEach((fn) =>
              fn({
                id: m.id as number,
                label: p.label,
                ...p.meta,
                messages: p.sent,
                opts: p.opts,
                startedAt: p.startedAt,
                done: true,
                error: m.message,
                ms: performance.now() - p.startedAt,
              }),
            );
          }
          p?.reject(err);
          pi?.reject(err);
        } else {
          this.loadWaiter?.reject(err);
          this.loadWaiter = null;
        }
        break;
      }
    }
  }

  private send(m: ToWorker) {
    this.worker.postMessage(m);
  }

  load(modelKey: string, h: LoadHandlers = {}): Promise<number> {
    if (this.loadWaiter) return Promise.reject(new Error("a load is already in flight"));
    return new Promise<number>((resolve, reject) => {
      this.loadWaiter = {
        // Recorded on SUCCESS only. A client that failed to load must not go on
        // stamping records with the weights it does not have.
        resolve: (ms) => {
          this.modelKey = modelKey;
          resolve(ms);
        },
        reject,
        h,
      };
      this.send({ type: "load", modelKey });
    });
  }

  /**
   * Watch every generation: what went in, what came out.
   *
   * Free's ask, 2026-07-27: "Whatever is being fed into the llm at any moment
   * is what I want to see on my screen then their response."
   *
   * THIS IS WHY IT LIVES HERE rather than at the call sites. There are eight
   * places that generate, and each assembles its own message array: `converse`
   * builds the system message for chat, `debate.ts` builds a different one for
   * the staging think, `contextFit` builds a third for the gist. A per-site
   * callback is a list you can forget to add to, and the one you forget is
   * invisible: the panel just quietly does not show that turn. Everything funnels
   * through this method, so tapping it is true by construction.
   *
   * Returns an unsubscribe function.
   */
  onGeneration(fn: (rec: GenerationRecord) => void): () => void {
    this.taps.add(fn);
    return () => {
      this.taps.delete(fn);
    };
  }

  /**
   * A label for the NEXT generation, so the panel can say which stage this was.
   *
   * Deliberately a one-shot property rather than a parameter on `generate`.
   * Threading a label through every call site is the same list-you-can-forget
   * problem the tap exists to avoid; an unlabelled turn still appears, just
   * without a name. Cleared on use so a stale label cannot mislabel the next one.
   */
  label(next: string, meta: TurnMeta = {}): this {
    this.pendingLabel = next;
    this.pendingMeta = meta;
    return this;
  }

  generate(
    messages: ChatMessage[],
    opts: GenerateOpts = {},
    h: StreamHandlers = {},
  ): Promise<{ text: string; stats: GenStats }> {
    const id = nextGenerationId++;
    const label = this.pendingLabel;
    const meta = { ...this.pendingMeta, model: this.modelKey ?? undefined };
    this.pendingLabel = undefined;
    this.pendingMeta = {};
    // Snapshot the messages. Callers reuse and mutate their arrays between
    // turns, and a transcript that changes after the fact is worse than none.
    //
    // The snapshot KEEPS `parts`, which say where each piece of a merged
    // message came from. The worker does not get them: it needs what the model
    // receives, and the parts are for a reader.
    const sent: ChatMessage[] = messages.map((m) => ({
      role: m.role,
      content: m.content,
      ...(m.parts ? { parts: m.parts.map((p) => ({ ...p })) } : {}),
    }));
    const startedAt = performance.now();
    this.taps.forEach((fn) =>
      fn({ id, label, ...meta, messages: sent, opts, startedAt, done: false }),
    );
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject, h, sent, opts, label, meta, startedAt });
      // Stripped: `parts` is inspection data and the wire protocol should
      // describe what the model gets, not what the UI would like to draw.
      this.send({
        type: "generate",
        id,
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        opts,
      });
    });
  }

  /**
   * Draw a picture. Only models with an image head can, and the worker says so
   * plainly rather than hanging if you ask one that cannot.
   *
   * No streaming callback, because there is nothing to stream: the image is a
   * fixed grid of tokens and is not a picture until the last one lands.
   */
  generateImage(
    prompt: string,
    opts: ImageOpts = {},
  ): Promise<{ images: ImageResult[]; ms: number }> {
    const id = nextGenerationId++;
    return new Promise((resolve, reject) => {
      this.pendingImages.set(id, { resolve, reject });
      this.send({ type: "generateImage", id, prompt, opts });
    });
  }

  interrupt() {
    this.send({ type: "interrupt" });
  }

  /** Drop the KV cache. Required whenever the conversation prefix is rewritten. */
  resetCache() {
    this.send({ type: "resetCache" });
  }

  dispose() {
    this.worker.terminate();
  }
}
